// Per-node suggestion contracts for the AI validation gate. Each custom editor
// node type gets a Zod schema over its SERIALIZED attributes (identical whether
// parsed from AI HTML data-* or PM JSON attrs) and an insertPolicy governing
// whether the AI gate admits NEW instances of the node in suggestion content:
//   - 'insertable'          — the AI may introduce new instances (condition mentions);
//   - 'replace-target-only' — admissible only as the target of replaceNode /
//                             setChoiceById, exactly as today;
//   - 'never'               — inadmissible anywhere the AI can reach.
//
// insertPolicy subsumes the former blanket SIMPLE_HTML_NODE_TYPES ban: a custom
// node is admissible in a text operation exactly when it carries a registered
// contract whose policy is 'insertable', so validation can enforce it — rather
// than every non-plain node being rejected outright.
//
// Attrs schemas are a permissive pass-through for every family except the
// condition mention: the tightened per-family schemas arrive with each Phase 3
// conversion PR, so replace-target ops on observations and validated tools keep
// behaving exactly as today. Zod objects strip unknown keys without erroring, so
// transient or not-yet-modeled attrs (openId, autofocus, loading, …) are
// tolerated rather than rejected.

import type { SeenMedplumClient } from '@seen/core/client'
import type { JsonObject } from '@seen/core/json'
import { z } from 'zod'
import {
  applyConditionMentionAccept,
  compensateConditionMentionAccept,
  validateConditionMentionAccept,
} from '#src/components/rich-text-editor/conditionMentionSideEffects.ts'

export type InsertPolicy = 'insertable' | 'replace-target-only' | 'never'

/*
 * Context handed to a family's `applyOnAccept` / `compensate` by the accept
 * pipeline (design §O.4). Carries the stamp identity the conditional create and
 * compensation key on, plus the FHIR client the side effect runs through.
 * Constructed per stamped node at accept time.
 */
export interface AcceptSideEffectCtx {
  medplum: SeenMedplumClient
  /* The accepting suggestion's id — the stamp scope. */
  suggestionId: string
  /*
   * The stamp node id (`${suggestionId}:${ordinal}`) the conditional create and
   * compensation key on. Stable across a re-insert; document positions are not.
   */
  nodeId: string
  /*
   * The edited resource's reference (e.g. `Composition/{id}`). The condition
   * family resolves its encounter from this to link diagnoses onto it; null
   * when the editor has no backing resource, in which case linkage is a no-op.
   */
  resourceRef: string | null
  /*
   * The participant whose note this is (`Patient/{id}`), so a family can refuse
   * a reference that belongs to a DIFFERENT patient's chart — the supply side
   * (getParticipantConditions) is already subject-scoped, so a mismatch here means a
   * hallucinated or miscopied id, exactly what accept-time validation exists to
   * stop before it becomes an Encounter.diagnosis link. Null when the editor has
   * no participant in scope, which skips the check.
   */
  patientRef: string | null
  /*
   * compensate-only: the references this accept linked, read from the receipt by
   * the undo-flip wiring and filtered to those no longer present in the doc, so
   * unlink-only compensation removes exactly the orphaned links (encounter
   * diagnosis links carry no stamp identity to resolve from FHIR alone).
   * Undefined for applyOnAccept.
   */
  linkedRefs?: string[]
}

/*
 * The attr patch a family's `applyOnAccept` returns: attrs to set on the stamped
 * node once its side effect completes (e.g. set `reference`, clear
 * `draftCondition`). `null` means "no patch" (a family with no attrs to write).
 */
export type AcceptAttrsPatch = JsonObject

/*
 * What a family's `applyOnAccept` returns: the attr patch, plus the ownership
 * marker for the receipt. `createdRef` names the reference whose durable
 * linkage THIS call genuinely created (the condition family: the
 * Encounter.diagnosis link it added) — the pipeline records exactly that into
 * the receipt's createdRefs, the set undo compensation is allowed to unlink. A
 * linkage that already existed before the accept (link-if-absent found it
 * present) is NOT the accept's to remove on undo, so the apply returns no
 * createdRef for it. `null` (the whole result) means the side effect did not
 * finalize — the node stays draft-flagged (R4).
 */
export interface AcceptApplyResult {
  patch: AcceptAttrsPatch | null
  createdRef?: string
}

/*
 * Context handed to a family's `validateOnAccept` by the accept pipeline's
 * accept-time validation phase (Option C, design State 1). Carries the FHIR
 * client the precondition read runs through and the participant the note
 * belongs to — validation is an existence / status / ownership check with no
 * stamp identity or resource linkage. AcceptSideEffectCtx is a structural
 * superset, so a family's side effect can reuse the same validate helper
 * directly.
 */
export interface AcceptValidateCtx {
  medplum: SeenMedplumClient
  // See AcceptSideEffectCtx.patientRef.
  patientRef: string | null
}

/*
 * A family's accept-time validation verdict. `{ ok: false }` refuses the accept
 * with a clinical reason surfaced to the dropped list — the content is never
 * inserted, stamped, or receipted. `retryable: true` marks a refusal whose
 * cause is TRANSIENT (the precondition read failed, not the precondition): the
 * pipeline aborts the accept but leaves the suggestion painted and pending, so
 * the clinician's next click retries — dropping it would attach a false
 * clinical explanation to a network hiccup.
 */
export type AcceptValidateResult =
  | { ok: true }
  | { ok: false; reason: string; retryable?: boolean }

export interface NodeContract {
  // PM node type name ('mention', 'observation', 'choice', …).
  type: string
  // Validates attrs parsed from AI- or island-edited HTML.
  attrs: z.ZodTypeAny
  insertPolicy: InsertPolicy
  // The ONLY place suggestion content produces side effects (design §O.3). Run
  // by the accept pipeline exactly once per stamped node, outside any React
  // lifecycle: create the resource via a conditional create keyed on the stamp
  // identity (R2, active-only), link it, and return the attr patch. Absent for
  // every family until its conversion PR (Phase 1.4+); the pipeline runs
  // correctly with none registered.
  applyOnAccept?: (
    attrs: JsonObject,
    ctx: AcceptSideEffectCtx
  ) => Promise<AcceptApplyResult | null>
  // Undo-after-accept compensation (R5), defined alongside `applyOnAccept` in
  // the same conversion PR. Resolves what was actually created by an
  // authoritative FHIR query on the stamp identity — NOT the receipt's
  // eventually-consistent refs (M4 blind spot) — and voids it.
  compensate?: (ctx: AcceptSideEffectCtx) => Promise<void>
  // Accept-time precondition validation (Option C, design State 1). Run by the
  // accept pipeline for each to-be-inserted node of this family BEFORE the
  // insert; a `{ ok: false }` verdict refuses the accept (dropped list + reason)
  // rather than committing content whose side effect would fail. Shares its
  // definition with `applyOnAccept`'s own precondition check (DRY). Absent → the
  // family needs no precondition and its accept stays synchronous.
  validateOnAccept?: (
    attrs: JsonObject,
    ctx: AcceptValidateCtx
  ) => Promise<AcceptValidateResult>
}

/* A resolved contract plus the family side-effect hooks the pipeline invokes. */
export interface ResolvedContract {
  attrs: z.ZodTypeAny
  insertPolicy: InsertPolicy
  subtype: string
  applyOnAccept?: NodeContract['applyOnAccept']
  compensate?: NodeContract['compensate']
  validateOnAccept?: NodeContract['validateOnAccept']
}

// A FHIR reference string — "ResourceType/<id>", optionally versioned with
// "/_history/<v>". Serialized on the node's data-reference attr.
const referenceString = z
  .string()
  .regex(
    /^[A-Za-z]+\/[A-Za-z0-9-.]+(\/_history\/[A-Za-z0-9-.]+)?$/,
    'not a FHIR reference string'
  )

const conditionRef = z
  .string()
  .regex(
    /^Condition\/[A-Za-z0-9-.]+(\/_history\/[A-Za-z0-9-.]+)?$/,
    'not a Condition reference'
  )

// Condition @-mention — the proving contract. Exactly one of a resolved,
// versioned reference (the frozen-snapshot pattern) XOR a draftCondition (a
// condition that does not exist yet). A reference-less, draft-less mention — the
// chip that blanks the editor on accept today — is unrepresentable.
export const conditionMentionAttrs = z
  .object({
    display: z.string().min(1),
    reference: conditionRef.optional().nullable(),
    draftCondition: z
      .object({ code: z.string().min(1), display: z.string().min(1) })
      .optional()
      .nullable(),
  })
  .refine((a) => !!a.reference !== !!a.draftCondition, {
    message: 'exactly one of reference | draftCondition',
  })

// Non-condition mentions (Task, Communication, ServiceRequest,
// DocumentReference, QuestionnaireResponse, …): a resolved reference + frozen
// display. Already state-complete, so most subtypes stay never-insertable —
// but Composition/QuestionnaireResponse/DocumentReference are pure citation
// links with no durable write on accept, so those three are insertable below
// when the ai-citation-mentions gate is on.
export const referenceMentionAttrs = z.object({
  display: z.string().min(1),
  reference: referenceString,
})

// A mention carrying no usable reference and no draft — what the AI emits when
// it fabricates a chip. Kept as an explicit shape so the gate can name the
// failure; never an admitted contract.
const anyMentionAttrs = z.object({
  display: z.string().optional().nullable(),
  reference: z.string().optional().nullable(),
})

// Every mention subtype shares the PM type name 'mention', so the contract is a
// discriminated union across subtypes keyed on the referenced resource type (or
// draftCondition for drafts). Condition mentions flip to 'insertable' in Phase
// 1.4, where the MentionNode gains draftCondition and applyOnAccept; the schema
// is registered here so it is ready. Composition, QuestionnaireResponse, and
// DocumentReference mentions are also insertable behind the ai-citation-mentions
// gate: each carries a resolved reference and a frozen display, and — unlike a
// condition mention, which links Encounter.diagnosis on accept — performs no
// durable write, so it needs no applyOnAccept/compensate/validateOnAccept hook.
// Every other reference type stays 'never'. A reference-less mention still resolves to the condition
// contract (not the permissive any-mention shape) so the replaceNode attrs check
// rejects it via the reference-XOR-draftCondition refine — that is what closes
// the reference-less-chip crash at 1.1, independent of insertPolicy.
export function mentionContractFor(
  reference: string | null | undefined,
  // The ai-note-suggestions gate (required, no default — every
  // caller must thread a real flag read so a missed call site fails to
  // compile instead of silently defaulting to the wrong value). Off: the
  // condition subtype falls back to 'never', identical to every other mention
  // subtype and to pre-campaign behavior — the AI can no longer introduce a
  // NEW condition mention via a text-insert op. This does not affect
  // replaceNode (which targets an EXISTING field by type, not insertPolicy)
  // or an already-accepted/versioned mention.
  noteSuggestionsEnabled: boolean,
  // The ai-citation-mentions gate (required, no default, same reason). Off: the
  // Composition/QuestionnaireResponse/DocumentReference citation subtypes fall
  // back to 'never' alongside every other reference type, which is pre-widening
  // behavior — the AI can no longer introduce a citation chip via a text-insert
  // op. Condition mentions are unaffected by this gate in either state.
  citationMentionsEnabled: boolean
): ResolvedContract {
  if (
    noteSuggestionsEnabled &&
    reference &&
    reference.startsWith('Condition/')
  ) {
    return {
      attrs: conditionMentionAttrs,
      // The 1.4b flip: the AI may now INSERT a reference-carrying condition
      // mention (this is the moment the no-create model goes live — the runtime,
      // linkage, sign gate, and compensation built in 1.4a all become reachable
      // from the AI producer). A versioned reference on the AI insert path is
      // rejected by the gate (validateSuggestionContent), not here, so the shared
      // schema keeps validating a clinician's versioned mention on the replaceNode
      // swap path (main parity).
      insertPolicy: 'insertable',
      subtype: 'condition',
      applyOnAccept: applyConditionMentionAccept,
      compensate: compensateConditionMentionAccept,
      validateOnAccept: validateConditionMentionAccept,
    }
  }
  if (reference && /^[A-Za-z]+\//.test(reference)) {
    const resourceType = reference.split('/')[0]
    const isInsertableCitation =
      citationMentionsEnabled &&
      (resourceType === 'Composition' ||
        resourceType === 'QuestionnaireResponse' ||
        resourceType === 'DocumentReference')
    return {
      attrs: referenceMentionAttrs,
      insertPolicy: isInsertableCitation ? 'insertable' : 'never',
      subtype: resourceType.toLowerCase(),
    }
  }
  return {
    attrs: conditionMentionAttrs,
    insertPolicy: 'never',
    subtype: 'unresolved',
  }
}

// Observation (BP / Number / Pain / Text / Choice render variants share one PM
// type). Permissive pass-through: the structured canonical value schema is a
// Phase 3 tightening. scaleType stays a bare string here so no live variant is
// rejected before that PR.
const observationAttrs = z.object({
  reference: referenceString.optional().nullable(),
  display: z.string().optional().nullable(),
  codingDisplay: z.string().optional().nullable(),
  system: z.string().optional().nullable(),
  code: z.string().optional().nullable(),
  scaleType: z.string().optional().nullable(),
  unit: z.string().optional().nullable(),
  answerOptions: z
    .array(z.object({ code: z.string(), display: z.string() }))
    .optional()
    .nullable(),
})

// Choice (template option buttons). Replace-target-only: setChoiceById targets
// an existing choice by fieldId; the AI never inserts a new one.
const choiceAttrs = z.object({
  options: z.string().optional().nullable(),
  dropdown: z.string().optional().nullable(),
  fieldId: z.string().optional().nullable(),
  display: z.string().optional().nullable(),
})

// ValidatedTool (Braden, etc.). Targets an existing form; never inserted.
const validatedToolAttrs = z.object({
  name: z.string().optional().nullable(),
  response: referenceString.optional().nullable(),
  restorationId: z.string().optional().nullable(),
})

const documentReferenceAttrs = z.object({
  reference: referenceString.optional().nullable(),
  display: z.string().optional().nullable(),
})

const signatureButtonAttrs = z.object({
  signature: referenceString.optional().nullable(),
  display: z.string().optional().nullable(),
})

const columnBlockAttrs = z.object({
  layout: z.string().optional().nullable(),
  columnWidths: z.array(z.number()).optional().nullable(),
})

const columnAttrs = z.object({
  width: z.string().optional().nullable(),
})

// Thin nodes carry no meaningful serialized state: block atoms with no attrs,
// and inline buttons whose only attr is a display label. Registered so every
// family has a contract, but never AI-insertable.
const emptyAttrs = z.object({})
const displayOnlyAttrs = z.object({
  display: z.string().optional().nullable(),
})

function neverContract(type: string, attrs: z.ZodTypeAny): NodeContract {
  return { type, attrs, insertPolicy: 'never' }
}

interface ContractsLookup {
  [key: string]: NodeContract
}

// Registry keyed by PM node type. 'mention' is special-cased via
// mentionContractFor(); its entry here is the fail-closed fallback.
export const contracts: ContractsLookup = {
  mention: { type: 'mention', attrs: anyMentionAttrs, insertPolicy: 'never' },
  observation: {
    type: 'observation',
    attrs: observationAttrs,
    insertPolicy: 'replace-target-only',
  },
  choice: {
    type: 'choice',
    attrs: choiceAttrs,
    insertPolicy: 'replace-target-only',
  },
  'validated-tool': {
    type: 'validated-tool',
    attrs: validatedToolAttrs,
    insertPolicy: 'replace-target-only',
  },
  'document-reference': neverContract(
    'document-reference',
    documentReferenceAttrs
  ),
  'signature-button': neverContract('signature-button', signatureButtonAttrs),
  encounter: neverContract('encounter', emptyAttrs),
  'encounter-medications-block': neverContract(
    'encounter-medications-block',
    emptyAttrs
  ),
  'conditions-addressed-block': neverContract(
    'conditions-addressed-block',
    emptyAttrs
  ),
  'add-timeline-note-button': neverContract(
    'add-timeline-note-button',
    displayOnlyAttrs
  ),
  'insert-allergies-button': neverContract(
    'insert-allergies-button',
    displayOnlyAttrs
  ),
  'insert-conditions-button': neverContract(
    'insert-conditions-button',
    displayOnlyAttrs
  ),
  'insert-current-conditions-button': neverContract(
    'insert-current-conditions-button',
    displayOnlyAttrs
  ),
  'insert-current-conditions-with-notes-button': neverContract(
    'insert-current-conditions-with-notes-button',
    displayOnlyAttrs
  ),
  'insert-medications-button': neverContract(
    'insert-medications-button',
    displayOnlyAttrs
  ),
  columnBlock: neverContract('columnBlock', columnBlockAttrs),
  column: neverContract('column', columnAttrs),
}

// Resolve the contract for a parsed custom node, dispatching mentions to their
// subtype. Returns null for a node type with no registered contract (fail-closed
// upstream: an unregistered custom node is treated as insertPolicy 'never').
export function contractForNode(
  type: string,
  attrs: JsonObject,
  noteSuggestionsEnabled: boolean,
  citationMentionsEnabled: boolean
): ResolvedContract | null {
  if (type === 'mention') {
    return mentionContractFor(
      attrs.reference as string | null | undefined,
      noteSuggestionsEnabled,
      citationMentionsEnabled
    )
  }
  const contract = contracts[type]
  if (!contract) {
    return null
  }
  return {
    attrs: contract.attrs,
    insertPolicy: contract.insertPolicy,
    subtype: type,
    applyOnAccept: contract.applyOnAccept,
    compensate: contract.compensate,
    validateOnAccept: contract.validateOnAccept,
  }
}
